// A refund names the Order lines the money returns against (2026-11-02).
//
// A staff refund used to be a number against a payment; squish-backend then
// guessed the line (an amount equal to exactly one line's balance landed
// there, anything else was spread pro-rata) and Order.Refunded_Amount__c and
// the receipt said whatever the guess said. Casey's launch-QA refund — $60 of
// a $72 payment, Business Premium + a domain — tracked right only because $60
// matched one line. Now the Refund modal lets the rep pick the line(s), and
// the request carries `lines: [{ lineId, amountCents }]` summing to the
// refund. What is here is the arithmetic and the joins, kept out of the JSX so
// test/refund-lines.test.js can pin them without a DOM.
//
// Two sources: the charge (from /api/accounts/:id/charges) carries the
// payment's booked ledger lines — `charge.lines`, one per OrderItem with what
// was paid and what has already gone back — and each refund on it the lines it
// named (`refund.lines`). The Orders carry the names (OrderItems → Product2).
// Joined on the OrderItem id.

import { receiptLines } from './orderReceipt.js';

const int = (v) => (Number.isFinite(Number(v)) ? Math.round(Number(v)) : 0);

// Every line of this payment a refund could be put against, in Order line
// order, with the name the receipt shows for it. availableCents is what the
// backend will accept for that line. Empty when the charge carries no booked
// lines (an unbooked payment, or an older backend): the modal then offers only
// the whole-payment refund it always did.
export function refundLineOptions(charge, orders) {
  const ledger = Array.isArray(charge?.lines) ? charge.lines.filter(l => l && l.lineId) : [];
  if (ledger.length === 0) return [];
  const pi = charge?.paymentIntentId;
  const named = new Map();
  for (const o of Array.isArray(orders) ? orders : []) {
    if (!pi || o?.Payment_Intent_ID__c !== pi) continue;
    for (const l of receiptLines(o)) if (l.id) named.set(l.id, { ...l, orderNumber: o.OrderNumber || null });
  }
  const byItem = ledger.map(l => {
    const item = l.orderItemId ? named.get(l.orderItemId) : null;
    const inflowCents = int(l.inflowCents);
    const refundedCents = int(l.refundedCents);
    const settled = (l.inflowState || 'settled') === 'settled';
    return {
      lineId: String(l.lineId),
      orderItemId: l.orderItemId || null,
      orderNumber: item?.orderNumber || null,
      productId: l.productId || null,
      sku: l.sku || null,
      name: item?.name || l.productId || l.sku || 'Line',
      detail: item?.detail || (item && l.productId && l.productId !== item.name ? l.productId : null),
      qty: item?.qty ?? null,
      inflowCents,
      refundedCents,
      availableCents: settled ? Math.max(0, inflowCents - refundedCents) : 0,
    };
  });
  // Order-line order where we know it; ledger order otherwise.
  const pos = new Map([...named.keys()].map((id, i) => [id, i]));
  return byItem.sort((a, b) => (pos.has(a.orderItemId) ? pos.get(a.orderItemId) : 1e9) - (pos.has(b.orderItemId) ? pos.get(b.orderItemId) : 1e9));
}

// What the rep picked, as the request body's `lines`. picks: { [lineId]:
// cents }. Lines picked at zero are not lines; null when nothing is picked,
// so the request is the whole-payment one.
export function refundLinesPayload(picks) {
  const out = Object.entries(picks || {})
    .map(([lineId, cents]) => ({ lineId, amountCents: int(cents) }))
    .filter(l => l.amountCents > 0);
  return out.length ? out : null;
}

// Why this pick cannot be sent, in the rep's words, or null when it can.
export function refundLinesProblem(picks, options, totalCents) {
  const lines = refundLinesPayload(picks);
  if (!lines) return 'Pick at least one line, or refund the whole payment.';
  const byId = new Map((options || []).map(o => [o.lineId, o]));
  for (const l of lines) {
    const o = byId.get(l.lineId);
    if (!o) return 'One of the picked lines is not on this payment.';
    if (l.amountCents > o.availableCents) return `${o.name} has only ${usd(o.availableCents)} left to refund.`;
  }
  const sum = lines.reduce((s, l) => s + l.amountCents, 0);
  if (Number.isFinite(totalCents) && sum !== totalCents) return `The lines add up to ${usd(sum)}, not ${usd(totalCents)}.`;
  return null;
}

// The amount a line-attributed refund is for: the sum of the picks.
export function refundLinesTotalCents(picks) {
  return (refundLinesPayload(picks) || []).reduce((s, l) => s + l.amountCents, 0);
}

// How a refund that named its lines reads on the receipt: "Business Premium
// $30.00 · Domain Registration $12.00". null for a refund that named none
// (an older refund, or one issued against the whole payment).
export function refundAttributionText(refund, options) {
  const lines = Array.isArray(refund?.lines) ? refund.lines : [];
  if (lines.length === 0) return null;
  const byId = new Map((options || []).map(o => [o.lineId, o]));
  return lines.map(l => {
    const o = byId.get(l.lineId);
    const name = o?.name || l.productId || l.sku || 'line';
    return `${name} ${usd(int(l.amountCents))}`;
  }).join(' · ');
}

// The POST /api/billing/refund body the modal sends. Pure so the two shapes —
// whole payment, and against named lines — are pinned side by side.
export function buildRefundRequest({ charge, cents, reason, note, approverEmail, picks = null }) {
  const lines = picks ? refundLinesPayload(picks) : null;
  return {
    paymentIntentId: charge.paymentIntentId,
    amountCents: cents,
    reason,
    note: note && note.trim() ? note.trim() : null,
    customerEmail: charge.customerEmail,
    approvedByEmail: approverEmail && approverEmail.trim() ? approverEmail.trim() : null,
    ...(lines ? { lines } : {}),
  };
}

function usd(cents) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((Number(cents) || 0) / 100);
}
